import { createSelector } from 'reselect';
import { filterMapByKeys } from 'lib/filter';
import { isNotEmpty } from 'lib/misc';
import {
  createBankTransactionIndex,
  createTransactionStateFilter,
  filterSearchIndex,
  getSearchResultEntities,
  getSearchResultMatches,
} from 'lib/search';
import { getSortedEntityList } from 'lib/sort';
import { getSelectorEntityId, getSelectorTransactionState } from 'lib/store';
import { filterTransactionsByIds, getTransactionId } from 'lib/transactions';
import { State } from 'store';
import { BankTransaction } from 'types/dto';
import { SearchMatchMap } from 'types/search';

const getBankTransactionState = (state: State) => state.transactions.bank;

// Raw store selectors
const getBankTransactionEntityMap = createSelector(
  getBankTransactionState,
  entities => entities.entities,
);
const getBankTransactionIds = createSelector(getBankTransactionState, entities => entities.ids);
export const getBankTransactionQuery = createSelector(
  getBankTransactionState,
  entities => entities.query,
);
const getBankTransactionSelected = createSelector(
  getBankTransactionState,
  entities => entities.selected,
);
export const getBankTransactionSortDirection = createSelector(
  getBankTransactionState,
  entities => entities.sortDir ?? undefined,
);
export const getBankTransactionSortKey = createSelector(
  getBankTransactionState,
  entities => entities.sortKey ?? undefined,
);
export const getBankTransactionStatus = createSelector(
  getBankTransactionState,
  entities => entities.status,
);

// Retrieve a bank transaction entity by its identifier
export const getBankTransaction = createSelector(
  getBankTransactionEntityMap,
  getSelectorEntityId,
  (entities, id) => (id ? entities[id] : undefined),
);

// Retrieve all available bank transactions enriched with associated bank account names
const getBankTransactions = createSelector(
  getBankTransactionEntityMap,
  getBankTransactionIds,
  (transactionMap, ids) => filterMapByKeys(transactionMap, ids).filter(isNotEmpty),
);

// Build bank transaction search index
const getBankTransactionSearchIndex = createSelector(
  getBankTransactions,
  createBankTransactionIndex,
);

// Retrieve bank transaction results that match the search query
const getBankTransactionResults = createSelector(
  getBankTransactions,
  getSelectorTransactionState,
  getBankTransactionSearchIndex,
  getBankTransactionQuery,
  (entities, transactionState, searchIndex, searchQuery) =>
    filterSearchIndex(
      searchIndex,
      searchQuery,
      entities,
      createTransactionStateFilter(transactionState),
    ),
);

// Retrieve sorted bank transactions that match the search query
export const getBankTransactionList = createSelector(
  getBankTransactionResults,
  getBankTransactionSortKey,
  getBankTransactionSortDirection,
  (results, sortKey, sortDir) => {
    const entities = getSearchResultEntities(results);
    const sorted = getSortedEntityList(entities, sortKey, sortDir, ['transactionDate']);
    const searchMatches = getSearchResultMatches(results, getTransactionId);

    return [sorted, searchMatches] as [Array<BankTransaction>, SearchMatchMap<BankTransaction>];
  },
);

// Get list of currently selected bank transactions
export const getSelectedBankTransactionList = createSelector(
  getBankTransactions,
  getBankTransactionSelected,
  filterTransactionsByIds,
);
